import { ReactNode } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { IoVideocamOutline } from 'react-icons/io5';

import { Box, Button, Group, Input, Modal, SimpleGrid, Stack, Text } from '@mantine/core';

import {
    Collateral,
    CollateralCategory,
    CollateralCategoryOptions,
    CollateralOnlyCategory,
    CollateralPayloadSchema,
    digitalConverter,
} from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';
import { FileHelpers } from 'helpers/FileHelpers';
import { FormatHelpers } from 'helpers/formatters';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { VideoHelpers } from 'helpers/VideoHelpers';

import { CollateralTeaser } from 'components/collateral/CollateralTeaser';
import { FileIcon } from 'components/file-icon/FileIcon';
import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { MultilineTextField } from 'components/forms/fields/MultilineTextField';
import { TextField } from 'components/forms/fields/TextField';
import { CollateralService } from 'services/CollateralService';
import { LocalNotificationService } from 'services/LocalNotificationService';

import cx from './CollateralModal.module.scss';

const CollateralFormSchema = CollateralPayloadSchema.pick({
    title: true,
    category: true,
    description: true,
});

const CollateralModal = ({
    collateral,
    href,
    justUploaded,
    onSaved,
    onClose,
}: {
    collateral: Collateral;
    href: string;
    justUploaded?: boolean;
    onSaved: () => Promise<unknown>;
    onClose: () => void;
}) => {
    const file = CollateralHelpers.file(collateral);
    const videoLink = CollateralHelpers.videoLink(collateral);

    const handleSave = async (values: {
        title: string;
        category?: CollateralCategory | null;
        description?: string | null;
    }) => {
        await CollateralService.update(collateral.id, {
            title: values.title,
            category: values.category || null,
            description: values.description?.trim() || null,
        });
        await onSaved();

        LocalNotificationService.showSuccess({ message: justUploaded ? 'Collateral added' : 'Collateral updated' });

        onClose();
    };

    return (
        <Modal opened onClose={onClose} title={justUploaded ? 'Name your collateral' : 'Edit collateral'} size="xl">
            <Form
                onSubmit={handleSave}
                defaultValues={{
                    title: collateral.title,
                    category: collateral.category ?? null,
                    description: collateral.description ?? '',
                }}
                zodSchema={CollateralFormSchema}
            >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
                    <Stack>
                        {justUploaded && (
                            <Text size="sm" c="dimmed">
                                Give it the name attendees will recognize on your booth, like the product name on the
                                brochure.
                            </Text>
                        )}

                        {file && (
                            <SourceCard
                                icon={<FileIcon mimeType={file.mimeType} size={20} />}
                                title={file.filename ?? 'File'}
                                meta={[
                                    file.filename?.split('.').pop()?.toUpperCase(),
                                    FileHelpers.isPdf(file) &&
                                        file.pageCount &&
                                        IntlHelpers.formatCount(file.pageCount, 'page', 'pages'),
                                    FormatHelpers.formatValue(file.filesize, digitalConverter),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                href={file.url ?? undefined}
                            />
                        )}

                        {videoLink && (
                            <SourceCard
                                icon={<IoVideocamOutline size={20} color="var(--mantine-color-violet-7)" />}
                                title={collateral.videoUrl ?? ''}
                                meta="Video link"
                                href={VideoHelpers.watchUrl(videoLink)}
                            />
                        )}

                        <TextField
                            labelProps={quietLabelProps}
                            label="Title"
                            name="title"
                            placeholder="eg. iC7 Hybrid String PCS fact sheet"
                            data-autofocus
                        />

                        <CategoryField />

                        <MultilineTextField
                            labelProps={quietLabelProps}
                            descriptionProps={quietDescriptionProps}
                            label="Short description"
                            description="Optional. One or two sentences shown under the title."
                            name="description"
                            placeholder="eg. Specs, efficiency curves and installation options at a glance"
                            maxLength={160}
                            autosize
                            minRows={2}
                        />

                        <FormSubmit>{justUploaded ? 'Save' : 'Save changes'}</FormSubmit>
                    </Stack>

                    <CollateralModalPreview collateral={collateral} href={href} />
                </SimpleGrid>
            </Form>
        </Modal>
    );
};

const quietLabelProps = { fz: 'md', mb: 4 };
const quietDescriptionProps = { fz: 'sm', c: 'dimmed', mb: 6 };

const CategoryField = () => {
    const { field } = useController({ name: 'category' });

    const marketingValues: string[] = Object.values(CollateralOnlyCategory);
    const groups = [
        {
            label: 'Marketing',
            options: CollateralCategoryOptions.filter(({ value }) => marketingValues.includes(value)),
        },
        {
            label: 'Technical',
            options: CollateralCategoryOptions.filter(({ value }) => !marketingValues.includes(value)),
        },
    ];

    return (
        <Input.Wrapper label="Type" labelProps={quietLabelProps}>
            <Stack gap={8} mt={4}>
                {groups.map((group) => (
                    <Box key={group.label}>
                        <Text size="xs" c="dimmed" mb={4}>
                            {group.label}
                        </Text>
                        <Group gap={4}>
                            {group.options.map(({ value, label }) => (
                                <Button
                                    key={value}
                                    size="compact-sm"
                                    radius="sm"
                                    fw={500}
                                    color="dark"
                                    variant={field.value === value ? 'filled' : 'default'}
                                    onClick={() => field.onChange(field.value === value ? null : value)}
                                >
                                    {label}
                                </Button>
                            ))}
                        </Group>
                    </Box>
                ))}
            </Stack>
        </Input.Wrapper>
    );
};

const SourceCard = ({ icon, title, meta, href }: { icon: ReactNode; title: string; meta: string; href?: string }) => (
    <Group gap="sm" wrap="nowrap" className={cx.sourceCard}>
        <Box className={cx.sourceIcon}>{icon}</Box>
        <Box miw={0} style={{ flex: 1 }}>
            <Text size="sm" fw={500} truncate>
                {title}
            </Text>
            <Text size="xs" c="dimmed">
                {meta}
            </Text>
        </Box>
        {href && (
            <Button
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                size="compact-xs"
            >
                Open
            </Button>
        )}
    </Group>
);

const CollateralModalPreview = ({ collateral, href }: { collateral: Collateral; href: string }) => {
    const { title, category } = useWatch();

    return (
        <Stack bg="gray.0" p="md" gap="xs" visibleFrom="sm" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
            <Text fz="xs" c="dimmed" fw={500}>
                Preview
            </Text>
            <Box maw={220} w="100%" mx="auto">
                <CollateralTeaser
                    collateral={{ ...collateral, category, title: title?.trim() || 'Add a title' }}
                    href={href}
                    layout="vertical"
                />
            </Box>
        </Stack>
    );
};

export { CollateralModal };
